import type { Vec2 } from '../shared/types';
import { pointInPolygon } from './geometry';

/**
 * Boolean operations on polygons with holes (Greiner–Hormann clipping,
 * generalised to multiple rings).
 *
 * A polygon is an outer ring plus zero or more hole rings. `polygonBoolean`
 * returns zero or more result polygons, each an outer ring with its holes;
 * unions may return disjoint polygons, intersections may be empty, differences
 * may split into pieces or carve holes.
 *
 * Robustness: each clip ring is expanded outward from its own centroid by a
 * sub-micron amount before clipping, which turns coincident edges and
 * shared-boundary touches into clean crossings (so unions of abutting shapes
 * merge); results are snapped back to a fine grid to erase the jitter.
 */

export type Op = 'union' | 'intersection' | 'difference';

export interface RingPoly {
  outer: Vec2[];
  holes: Vec2[][];
}

interface V {
  x: number;
  y: number;
  next: V;
  prev: V;
  corr: V | null;
  inter: boolean;
  entry: boolean;
  visited: boolean;
  alpha: number;
}

const PERTURB = 1e-4;
const SNAP = 1e3;
const snap = (n: number): number => Math.round(n * SNAP) / SNAP;

function mkVertex(x: number, y: number, inter = false, alpha = 0): V {
  return { x, y, next: null as any, prev: null as any, corr: null, inter, entry: false, visited: false, alpha };
}

function signedArea(ring: Vec2[]): number {
  let a = 0;
  for (let i = 0; i < ring.length; i++) {
    const p = ring[i], q = ring[(i + 1) % ring.length];
    a += p.x * q.y - q.x * p.y;
  }
  return a / 2;
}

/** Build a circular doubly-linked ring; returns its vertices in order. */
function buildRing(ring: Vec2[]): V[] {
  const vs = ring.map((p) => mkVertex(p.x, p.y));
  const n = vs.length;
  for (let i = 0; i < n; i++) {
    vs[i].next = vs[(i + 1) % n];
    vs[i].prev = vs[(i - 1 + n) % n];
  }
  return vs;
}

function insertBetween(v: V, start: V, end: V): void {
  let curr = start.next;
  while (curr !== end && curr.inter && curr.alpha < v.alpha) curr = curr.next;
  v.next = curr;
  v.prev = curr.prev;
  curr.prev.next = v;
  curr.prev = v;
}

function segIntersect(a: V, b: V, c: V, d: V): { t: number; u: number; x: number; y: number } | null {
  const rx = b.x - a.x, ry = b.y - a.y;
  const sx = d.x - c.x, sy = d.y - c.y;
  const denom = rx * sy - ry * sx;
  if (Math.abs(denom) < 1e-12) return null;
  const t = ((c.x - a.x) * sy - (c.y - a.y) * sx) / denom;
  const u = ((c.x - a.x) * ry - (c.y - a.y) * rx) / denom;
  if (t <= 0 || t >= 1 || u <= 0 || u >= 1) return null;
  return { t, u, x: a.x + t * rx, y: a.y + t * ry };
}


/** Even-odd test of a point against a whole operand (all its rings). */
function insideOperand(p: { x: number; y: number }, rings: V[][]): boolean {
  let inside = false;
  for (const r of rings) if (pointInPolygon(p, r)) inside = !inside;
  return inside;
}

function cleanRing(ring: Vec2[]): Vec2[] {
  const out: Vec2[] = [];
  for (const p of ring) {
    const q = { x: snap(p.x), y: snap(p.y) };
    const last = out[out.length - 1];
    if (!last || last.x !== q.x || last.y !== q.y) out.push(q);
  }
  while (out.length > 1 && out[0].x === out[out.length - 1].x && out[0].y === out[out.length - 1].y) out.pop();
  return out;
}

/** Expand a ring outward from its centroid by PERTURB (breaks degeneracies). */
function perturbOut(ring: Vec2[]): Vec2[] {
  let gx = 0, gy = 0;
  for (const p of ring) { gx += p.x; gy += p.y; }
  gx /= ring.length; gy /= ring.length;
  return ring.map((p) => {
    const dx = p.x - gx, dy = p.y - gy;
    const d = Math.hypot(dx, dy) || 1;
    return { x: p.x + (dx / d) * PERTURB, y: p.y + (dy / d) * PERTURB };
  });
}

/** A point guaranteed to lie inside a simple ring (just right of its leftmost vertex). */
function interiorPoint(ring: Vec2[]): { x: number; y: number } {
  let m = ring[0];
  for (const p of ring) if (p.x < m.x) m = p;
  return { x: m.x + 1e-3, y: m.y };
}

/** Group result rings into outer + holes by even-odd nesting depth. */
function groupRings(rings: Vec2[][]): RingPoly[] {
  const reps = rings.map(interiorPoint);
  const depth = rings.map((_, i) => {
    let d = 0;
    for (let j = 0; j < rings.length; j++) if (j !== i && pointInPolygon(reps[i], rings[j])) d++;
    return d;
  });
  // Even depth → outer (a new polygon); odd depth → hole of its nearest container.
  const polys: RingPoly[] = [];
  const outerIndex = new Map<number, number>(); // ring index → polys index
  rings.forEach((ring, i) => {
    if (depth[i] % 2 === 0) {
      outerIndex.set(i, polys.length);
      polys.push({ outer: ring, holes: [] });
    }
  });
  rings.forEach((ring, i) => {
    if (depth[i] % 2 === 0) return;
    // nearest container = smallest-area ring (by |area|) that contains this one
    let best = -1, bestArea = Infinity;
    for (let j = 0; j < rings.length; j++) {
      if (j === i || !pointInPolygon(reps[i], rings[j])) continue;
      const a = Math.abs(signedArea(rings[j]));
      if (a < bestArea) { bestArea = a; best = j; }
    }
    const oi = best >= 0 ? outerIndex.get(best) : undefined;
    if (oi !== undefined) polys[oi].holes.push(ring);
    else polys.push({ outer: ring, holes: [] }); // orphan: treat as its own shape
  });
  return polys;
}

export function polygonBoolean(subject: RingPoly, clip: RingPoly, op: Op): RingPoly[] {
  const subjRings = [subject.outer, ...subject.holes].filter((r) => r.length >= 3);
  const clipRings = [clip.outer, ...clip.holes].filter((r) => r.length >= 3).map(perturbOut);
  if (!subjRings.length || !clipRings.length) return [];

  const subjV = subjRings.map(buildRing);
  const clipV = clipRings.map(buildRing);

  // Phase 1: intersect every subject edge against every clip edge.
  let found = false;
  for (const S of subjV) {
    for (let i = 0; i < S.length; i++) {
      const s1 = S[i], s2 = S[(i + 1) % S.length];
      for (const C of clipV) {
        for (let j = 0; j < C.length; j++) {
          const c1 = C[j], c2 = C[(j + 1) % C.length];
          const hit = segIntersect(s1, s2, c1, c2);
          if (!hit) continue;
          const a = mkVertex(hit.x, hit.y, true, hit.t);
          const b = mkVertex(hit.x, hit.y, true, hit.u);
          a.corr = b; b.corr = a;
          insertBetween(a, s1, s2);
          insertBetween(b, c1, c2);
          found = true;
        }
      }
    }
  }

  const baseSf = op === 'intersection';
  const baseCf = op === 'intersection' || op === 'difference';

  const out: Vec2[][] = [];

  // Rings with no crossings are kept or dropped purely by containment.
  const keepWhole = (ringV: V[], otherRings: V[][], asClip: boolean): boolean => {
    const inOther = insideOperand(ringV[0], otherRings);
    if (op === 'union') return !inOther;
    if (op === 'intersection') return inOther;
    // difference: keep subject parts outside clip; keep clip parts inside subject (as holes)
    return asClip ? inOther : !inOther;
  };
  // Intersection vertices live only in the linked list, so traverse it (not the
  // original-vertex array) to tell whether a ring took part in any crossing.
  const ringHasInter = (first: V): boolean => {
    let p = first.next;
    while (p !== first) { if (p.inter) return true; p = p.next; }
    return first.inter;
  };

  if (found) {
    // Phase 2: mark entry/exit per ring (alternating from each ring's first vertex).
    for (const S of subjV) {
      if (!ringHasInter(S[0])) continue;
      let sf = baseSf !== insideOperand(S[0], clipV);
      let v = S[0];
      do { if (v.inter) { v.entry = sf; sf = !sf; } v = v.next; } while (v !== S[0]);
    }
    for (const C of clipV) {
      if (!ringHasInter(C[0])) continue;
      let cf = baseCf !== insideOperand(C[0], subjV);
      let w = C[0];
      do { if (w.inter) { w.entry = cf; cf = !cf; } w = w.next; } while (w !== C[0]);
    }

    // Phase 3: trace, hopping between operands at each intersection.
    const allInter: V[] = [];
    for (const S of subjV) { let p = S[0]; do { if (p.inter) allInter.push(p); p = p.next; } while (p !== S[0]); }
    for (const start of allInter) {
      if (start.visited) continue;
      const ring: Vec2[] = [{ x: start.x, y: start.y }];
      let curr = start;
      do {
        curr.visited = true;
        if (curr.corr) curr.corr.visited = true;
        if (curr.entry) {
          do { curr = curr.next; ring.push({ x: curr.x, y: curr.y }); } while (!curr.inter);
        } else {
          do { curr = curr.prev; ring.push({ x: curr.x, y: curr.y }); } while (!curr.inter);
        }
        curr = curr.corr!;
      } while (!curr.visited);
      const cleaned = cleanRing(ring);
      if (cleaned.length >= 3) out.push(cleaned);
    }
  }

  // Add non-intersecting rings of each operand by containment.
  for (let k = 0; k < subjV.length; k++) {
    if (found && ringHasInter(subjV[k][0])) continue;
    if (keepWhole(subjV[k], clipV, false)) out.push(cleanRing(subjRings[k]));
  }
  for (let k = 0; k < clipV.length; k++) {
    if (found && ringHasInter(clipV[k][0])) continue;
    if (keepWhole(clipV[k], subjV, true)) out.push(cleanRing(clipRings[k]));
  }

  return groupRings(out.filter((r) => r.length >= 3));
}
